type ColorArray = [number, number, number, number];

interface ArcGISSymbol {
  type: string;
  color?: ColorArray;
  outline?: {
    color?: ColorArray;
    style?: string;
  };
  imageData?: string;
  contentType?: string;
}

interface UniqueValueInfo {
  value: string;
  label: string;
  symbol: ArcGISSymbol;
}

interface ArcGISRenderer {
  type: string;
  symbol?: ArcGISSymbol;
  uniqueValueInfos?: UniqueValueInfo[];
}

export interface DynamicLegendItem {
  label: string;
  /** CSS rgba string for fill/marker symbols */
  fillColor?: string;
  /** CSS rgba string for the symbol outline — use as CSS border color */
  outlineColor?: string;
  /** data: URL (base64) for picture marker/fill symbols */
  imageDataUrl?: string;
}

function toRgba(color: ColorArray): string {
  return `rgba(${color[0]},${color[1]},${color[2]},${(color[3] / 255).toFixed(3)})`;
}

function symbolToLegendItem(label: string, symbol: ArcGISSymbol): DynamicLegendItem {
  // Picture marker/fill → return base64 image
  if (symbol.type === 'esriPMS' || symbol.type === 'esriPFS') {
    return {
      label,
      imageDataUrl: symbol.imageData
        ? `data:${symbol.contentType ?? 'image/png'};base64,${symbol.imageData}`
        : undefined,
    };
  }

  // Simple fill/marker → extract fill color and outline
  const fillColor = symbol.color ? toRgba(symbol.color) : undefined;
  const outline = symbol.outline;
  const hasVisibleOutline =
    outline && outline.style !== 'esriSLSNull' && outline.color;
  const outlineColor = hasVisibleOutline ? toRgba(outline.color!) : undefined;

  return { label, fillColor, outlineColor };
}

/** Module-level cache keyed by layer URL — each layer is fetched at most once per session */
const layerRendererCache = new Map<string, DynamicLegendItem[]>();

/**
 * Fetches the layer's renderer from the ArcGIS MapServer layer JSON and returns
 * a list of legend items. Supports uniqueValue and simple renderers.
 * Results are cached for the session.
 */
export async function fetchLayerDynamicLegend(layerUrl: string): Promise<DynamicLegendItem[]> {
  if (layerRendererCache.has(layerUrl)) return layerRendererCache.get(layerUrl)!;

  const resp = await fetch(`${layerUrl}?f=json`);
  const data: { name?: string; drawingInfo?: { renderer?: ArcGISRenderer } } =
    await resp.json();

  const renderer = data.drawingInfo?.renderer;
  let items: DynamicLegendItem[] = [];

  if (!renderer) {
    // no-op — leave items empty
  } else if (renderer.type === 'uniqueValue') {
    items = (renderer.uniqueValueInfos ?? [])
      .filter((info) => info.label && info.label.trim() !== '')
      .map((info) => symbolToLegendItem(info.label, info.symbol));
  } else if (renderer.type === 'simple' && renderer.symbol) {
    items = [symbolToLegendItem(data.name ?? 'Parking', renderer.symbol)];
  }

  layerRendererCache.set(layerUrl, items);
  return items;
}

